// Store-less (WooCommerce-free) account detection and the resulting
// "site-tools-only" app mode. See sukow/payhelm#730.
//
// A store-less account is a WordPress site running the PayHelm plugin WITHOUT
// WooCommerce installed: it can use the AI site-editing tools (posts/pages,
// media, theme/CSS, pixel, settings) but has no WooCommerce REST API to key
// into, so there is no order/product data to import or chart. The backend flags
// these accounts by returning `store_connected: false` on the WooCommerce
// account (`consumer_key IS NULL`); every other store channel is inherently a
// connected store and omits the field. In site-tools-only mode the app surfaces
// the AI assistant instead of empty analytics.

export interface AccountLike {
  channel?: string
  is_store_marketplace_account?: boolean
  // MySQL/knex may serialize `(consumer_key IS NOT NULL)` as boolean or 0/1.
  store_connected?: boolean | number | string | null
  [key: string]: unknown
}

/** WooCommerce / WordPress plugin accounts (case-insensitive channel aliases). */
export const isWooCommerceChannel = (account?: AccountLike | null): boolean => {
  const ch = String(account?.channel || '').toLowerCase()
  return ch === 'woocommerce' || ch === 'wooc'
}

/** True when papaya flags the Woo account as having no consumer keys. */
const isStoreDisconnected = (value: unknown): boolean =>
  value === false || value === 0 || value === '0'

/** A WooCommerce site running the plugin without WooCommerce (no connected store). */
export const isStoreLessAccount = (account?: AccountLike | null): boolean =>
  isWooCommerceChannel(account) && isStoreDisconnected(account?.store_connected)

/**
 * True when the user has at least one real, connected storefront/marketplace.
 * `store_connected` is only emitted for WooCommerce; every other store channel
 * (Shopify, Bigcommerce, ...) is inherently connected, so it counts here. A
 * reconnecting WooCommerce store reads as connected again as soon as its keys
 * arrive.
 */
export const hasConnectedStore = (accounts?: AccountLike[] | null): boolean =>
  (accounts ?? []).some((a) => a?.is_store_marketplace_account && !isStoreLessAccount(a))

/**
 * "Site-tools-only" mode: the user has a store/marketplace account, but none of
 * them is a connected store (every one is a store-less WooCommerce install).
 * This gates the app into the AI site-editing experience and away from the
 * empty analytics pages.
 */
export const isSiteToolsOnly = (accounts?: AccountLike[] | null): boolean => {
  const list = accounts ?? []
  return list.some((a) => a?.is_store_marketplace_account) && !hasConnectedStore(list)
}
